import { defineItem } from "./items/define";
import { defineModule } from "./modules/define";
import { ICE_J_PER_KG_K, ROCK_J_PER_KG_K } from "./thermal";
import type { ItemDef, ItemCategory, ModuleDef, Stats } from "./types";

interface Material {
  resource: string;
  blockId: string;
  chunkId: string;
  blockName: string;
  chunkName: string;
  blockSprite: string;
  chunkCategory: ItemCategory;
  chunkPalette: {
    veinDark: string;
    vein: string;
    frameDark?: string;
    frame?: string;
  };

  block: Partial<Stats>;
  chunk: Partial<Stats>;
}

export const MATERIALS: Material[] = [
  {
    resource: "rock",
    blockId: "rock_block",
    chunkId: "chunk_rock",
    blockName: "Rock",
    chunkName: "Rock Chunk",
    blockSprite: "rock_block",
    chunkCategory: "rock",
    chunkPalette: { veinDark: "#1f2027", vein: "#3a3d48" },
    block: { mass: 100, hp: 8, tensile: 30_000_000, hardness: 0.5, thermalConductivity: 0.75, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.85, meltPointK: 1_500 },
    chunk: { mass: 1, hp: 4, tensile: 20_000_000, hardness: 0.5, temperature: 180, valuePerKg: 0.1, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.85, meltPointK: 1_500 },
  },
  {
    resource: "iron",
    blockId: "iron_ore_block",
    chunkId: "chunk_iron",
    blockName: "Iron Ore Block",
    chunkName: "Iron Chunk",
    blockSprite: "iron_ore_block",
    chunkCategory: "ore",
    chunkPalette: { veinDark: "#3a2a18", vein: "#5e442b" },
    block: { mass: 150, hp: 10, tensile: 50_000_000, hardness: 0.55, thermalConductivity: 1.1, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.8, meltPointK: 1_800 },
    chunk: { mass: 1, hp: 4, tensile: 30_000_000, hardness: 0.55, temperature: 190, valuePerKg: 5, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.8, meltPointK: 1_800 },
  },
  {
    resource: "copper",
    blockId: "copper_ore_block",
    chunkId: "chunk_copper",
    blockName: "Copper Ore Block",
    chunkName: "Copper Chunk",
    blockSprite: "copper_ore_block",
    chunkCategory: "ore",
    chunkPalette: { veinDark: "#3d1f10", vein: "#6a3a1c" },
    block: { mass: 130, hp: 11, tensile: 35_000_000, hardness: 0.4, thermalConductivity: 1.4, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.75, meltPointK: 1_700 },
    chunk: { mass: 1, hp: 4, tensile: 25_000_000, hardness: 0.4, temperature: 190, valuePerKg: 8, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.75, meltPointK: 1_700 },
  },
  {
    resource: "uranium",
    blockId: "uranium_ore_block",
    chunkId: "chunk_uranium",
    blockName: "Uranium Ore Block",
    chunkName: "Uranium Chunk",
    blockSprite: "uranium_ore_block",
    chunkCategory: "ore",
    chunkPalette: { veinDark: "#1c4530", vein: "#326b48" },
    block: { mass: 200, hp: 14, tensile: 40_000_000, hardness: 0.55, thermalConductivity: 0.7, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.8, meltPointK: 1_500 },
    chunk: { mass: 1, hp: 4, tensile: 25_000_000, hardness: 0.55, temperature: 220, valuePerKg: 80, thermalInertia: ROCK_J_PER_KG_K, thermalEmissivity: 0.8, meltPointK: 1_500 },
  },
  {
    resource: "water",
    blockId: "ice_block",
    chunkId: "chunk_water",
    blockName: "Water Ice Block",
    chunkName: "Water Ice Chunk",
    blockSprite: "ice_block",
    chunkCategory: "ice",
    chunkPalette: { veinDark: "#5b7a96", vein: "#7ea0bd", frameDark: "#0e1a26", frame: "#3e5973" },
    block: { mass: 60, hp: 6, tensile: 1_000_000, hardness: 0.1, thermalConductivity: 0.4, thermalInertia: ICE_J_PER_KG_K, thermalEmissivity: 0.97, meltPointK: 350 },
    chunk: { mass: 1, hp: 3, tensile: 500_000, hardness: 0.1, temperature: 90, valuePerKg: 2, thermalInertia: ICE_J_PER_KG_K, thermalEmissivity: 0.97, meltPointK: 350 },
  },
];

export const MATERIAL_BLOCKS: ModuleDef[] = MATERIALS.map((m) =>
  defineModule({
    id: m.blockId,
    name: m.blockName,
    category: "rock",
    tier: 1,
    cells: { w: 1, h: 1 },
    sprite: m.blockSprite,
    stats: m.block,
    mineable: { resource: m.resource, debrisItemId: m.chunkId },
  }),
);

export const MATERIAL_CHUNKS: ItemDef[] = MATERIALS.map((m) =>
  defineItem({
    id: m.chunkId,
    name: m.chunkName,
    category: m.chunkCategory,
    sizeM: 2,
    sprite: m.chunkId,
    stats: m.chunk,
    resource: m.resource,
  }),
);
